import React from 'react'
import DatePicker from 'react-datepicker'
import TelephoneInput from 'react-phone-number-input/input'
import { subYears } from 'date-fns'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import ClearableInput, {
  SettingsField,
} from '../../../components/Settings/ClearableInput'
import Icon from '../../../components/icon'
import LocationSearch from '../../../components/location-search'
import SettingsCard from '../../../components/Settings/SettingsCard'
import { formatPhoneNumberForDisplay } from '../../../lib/phone'

/**
 * The account form fields, re-parented into the card layout.
 *
 * Every control is the one the page already used — LocationSearch, DatePicker,
 * TelephoneInput, native selects. Only the surrounding markup changed.
 */
export default function AccountFieldsCard({
  user,
  setUser,
  setHasUserChanged,
  email,
  setEmail,
  locale,
  shirtSizes,
  nameRef,
  currentUser,
  checkingNickname,
  nicknameTaken,
  emailVerificationError,
  onEmailVerifyRequest,
  onChangeNickname,
  onLocationChange,
  onSchoolChange,
  onSave,
  saveDisabled,
  isSaving,
}) {
  const { t } = useTranslation()

  const update = patch => {
    setUser({ ...user, ...patch })
    setHasUserChanged(true)
  }

  // The API has historically stored Spanish gender values. Map them onto the
  // English option values so a controlled <select> still matches.
  const GENDER_ALIASES = { Hombre: 'Male', Mujer: 'Female', Otro: 'Other' }
  const genderValue = GENDER_ALIASES[user?.gender] ?? user?.gender ?? ''

  // Must prefer shirt_size_id: it's what onChange writes, so reading only the
  // nested shirt_size object would freeze the select on its initial value.
  const shirtSizeValue = user?.shirt_size_id ?? user?.shirt_size?.id ?? ''

  const nicknameChanged =
    user?.nickname && user.nickname !== currentUser?.nickname

  let nicknameHelp = null
  let nicknameHelpVariant
  if (checkingNickname) {
    nicknameHelp = t('users.edit.nickname_checking', 'Checking availability...')
  } else if (nicknameChanged && nicknameTaken) {
    nicknameHelp = t('users.edit.nickname_taken', 'Username already taken')
    nicknameHelpVariant = 'error'
  } else if (nicknameChanged && !nicknameTaken) {
    nicknameHelp = t('users.edit.nickname_available', 'Username available')
    nicknameHelpVariant = 'success'
  }

  return (
    <SettingsCard icon="user" title={t('users.edit.user_profile')}>
      <div className="row">
        <div className="col-md-6">
          <SettingsField
            label={t('devise.sessions.fullname')}
            htmlFor="fullName"
          >
            <ClearableInput
              id="fullName"
              name="name"
              value={user?.name ?? ''}
              inputRef={nameRef}
              onChange={name => update({ name })}
              onClear={() => update({ name: '' })}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={
              <>
                {t('devise.sessions.email')}
                {user?.email_verified_at ? (
                  <span className="verified-account">
                    <Icon name="user-check" /> {t('users.edit.email_verified')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={onEmailVerifyRequest}
                  >
                    <Icon name="envelope" />{' '}
                    {emailVerificationError
                      ? t('users.edit.email_verify_failed')
                      : t('users.edit.email_verify_prompt')}
                  </button>
                )}
              </>
            }
            htmlFor="email"
          >
            <ClearableInput
              id="email"
              name="email"
              type="email"
              value={email ?? ''}
              onChange={setEmail}
              onClear={() => setEmail('')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('devise.sessions.username')}
            htmlFor="nickname"
            help={nicknameHelp}
            helpVariant={nicknameHelpVariant}
          >
            <div className="input-group">
              <span className="input-group-text">@</span>
              <input
                className="form-control"
                id="nickname"
                name="nickname"
                type="text"
                value={user?.nickname || ''}
                onChange={e => onChangeNickname(e.target.value)}
                autoCapitalize="none"
              />
            </div>
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.language')} htmlFor="language">
            <select
              name="language"
              id="language"
              className="form-select"
              value={user?.language ?? 'en'}
              onChange={e => update({ language: e.target.value })}
            >
              <option value="en">{t('users.edit.english')}</option>
              <option value="es">{t('users.edit.spanish')}</option>
            </select>
          </SettingsField>
        </div>

        <div className="col-12">
          <SettingsField
            label={t('users.edit.location')}
            htmlFor="autocomplete"
          >
            <LocationSearch
              onChange={onLocationChange}
              placeholder={t('users.edit.search')}
              value={user?.location}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.birthday')} htmlFor="birthDate">
            <DatePicker
              className="form-control"
              dateFormat={t('config.date_picker')}
              id="birthDate"
              maxDate={subYears(new Date(), 13)}
              name="birthday"
              placeholderText={t('users.edit.no_birthday')}
              selected={user?.birthday ? moment(user?.birthday).toDate() : null}
              shouldCloseOnSelect
              wrapperClassName="d-block"
              onChange={date =>
                update({
                  birthday: date ? moment(date).format('YYYY-MM-DD') : null,
                })
              }
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.phone')} htmlFor="phone">
            <TelephoneInput
              className="form-control"
              id="phone"
              country="US"
              initialValueFormat="national"
              international={false}
              onChange={phone => update({ phone })}
              placeholder="(123) 456-7890"
              value={formatPhoneNumberForDisplay(user?.phone)}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.gender')} htmlFor="gender">
            <select
              name="gender"
              id="gender"
              className="form-select"
              value={genderValue}
              onChange={e => update({ gender: e.target.value })}
            >
              <option value="">{t('users.edit.no_gender')}</option>
              <option value="Male">{t('users.edit.male')}</option>
              <option value="Female">{t('users.edit.female')}</option>
              <option value="Other">{t('users.edit.other_gender')}</option>
            </select>
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.t_shirt')} htmlFor="tShirt">
            <select
              name="T-Shirt"
              id="tShirt"
              className="form-select"
              value={shirtSizeValue}
              onChange={({ target }) => update({ shirt_size_id: target.value })}
            >
              <option value="">{t('users.edit.no_gender')}</option>
              {shirtSizes ? (
                shirtSizes.map(shirtSize => (
                  <option key={shirtSize.id} value={shirtSize.id}>
                    {shirtSize[`${locale}_name`]}
                  </option>
                ))
              ) : (
                <option disabled>loading...</option>
              )}
            </select>
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField label={t('users.edit.school')} htmlFor="school">
            <LocationSearch
              onChange={onSchoolChange}
              placeholder={t('users.edit.search')}
              value={user?.school}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('users.edit.school_type')}
            htmlFor="school_type"
            help={
              user?.school?.trim() && !user?.school_type?.trim()
                ? t('users.edit.school_type_required')
                : null
            }
          >
            <select
              name="school_type"
              id="school_type"
              className="form-select"
              value={user?.school_type ?? ''}
              onChange={e => update({ school_type: e.target.value })}
            >
              <option value="">{t('users.edit.school_type_none')}</option>
              <option value="Intermediate School">
                {t('users.edit.school_type_intermediate')}
              </option>
              <option value="High School">
                {t('users.edit.school_type_high_school')}
              </option>
              <option value="College">
                {t('users.edit.school_type_college')}
              </option>
            </select>
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('users.edit.work_place')}
            htmlFor="work_place"
          >
            <ClearableInput
              id="work_place"
              name="work_place"
              value={user?.work_place || ''}
              onChange={work_place => update({ work_place })}
              onClear={() => update({ work_place: '' })}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('users.edit.work_title')}
            htmlFor="work_title"
          >
            <ClearableInput
              id="work_title"
              name="work_title"
              value={user?.work_title || ''}
              onChange={work_title => update({ work_title })}
              onClear={() => update({ work_title: '' })}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="retired"
              checked={!!user?.retired}
              onChange={e => update({ retired: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="retired">
              {t('users.edit.retired')}
            </label>
          </div>
        </div>

        <div className="col-12 mt-3">
          <button
            className="btn btn-primary"
            id="settings-form-submit"
            type="button"
            onClick={onSave}
            disabled={saveDisabled}
          >
            {isSaving ? t('users.edit.saving') : t('users.edit.save')}
          </button>
        </div>
      </div>
    </SettingsCard>
  )
}

AccountFieldsCard.propTypes = {
  user: PropTypes.object,
  setUser: PropTypes.func.isRequired,
  setHasUserChanged: PropTypes.func.isRequired,
  email: PropTypes.string,
  setEmail: PropTypes.func.isRequired,
  locale: PropTypes.string,
  shirtSizes: PropTypes.array,
  nameRef: PropTypes.object,
  currentUser: PropTypes.object,
  checkingNickname: PropTypes.bool,
  nicknameTaken: PropTypes.bool,
  emailVerificationError: PropTypes.bool,
  onEmailVerifyRequest: PropTypes.func.isRequired,
  onChangeNickname: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  onSchoolChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  isSaving: PropTypes.bool,
}
